import React, { useState } from 'react';

import { AddIcon, Button, DeleteIcon, Text, dialogService } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';

import { Table } from '~/components/common';

import { getVenderSupplierRegisterManagerInfoColumnDefs } from '~/utils';

import { VenderSupplierManagerAddModal, VenderSupplierManagerModifyModal } from '..';

import type { TypeManagerRowInfo, TypeOwnerSupplierManager, TypeSupplierAddFormValue } from '~/types/vender.types';

import styles from './VenderSupplierAddModalManagerInfoSection.module.scss';

type TypeVenderSupplierAddModalManagerInfoSectionProps = {
  venderSupplierAddForm: TypeSupplierAddFormValue;
  setVenderSupplierAddForm: React.Dispatch<React.SetStateAction<TypeSupplierAddFormValue>>;
};

/**
 * [화주사정보관리 > 공급사관리] 공급사 등록 모달 컴포넌트 > 담당자 정보 영역
 */
const VenderSupplierAddModalManagerInfoSection = ({
  venderSupplierAddForm,
  setVenderSupplierAddForm,
}: TypeVenderSupplierAddModalManagerInfoSectionProps) => {
  const [selectedManagerInfo, setSelectedManagerInfo] = useState<TypeManagerRowInfo | null>(null);
  const [selectedManagerRowsList, setSelectedManagerRowsList] = useState<IRowNode<TypeOwnerSupplierManager>[]>([]);

  const [isVenderSupplierManagerAddModalOpen, setIsVenderSupplierManagerAddModalOpen] = useState(false);
  const [isVenderSupplierManagerModifyModalOpen, setIsVenderSupplierManagerModifyModalOpen] = useState(false);

  const { gridOptions, columnDefs } = getVenderSupplierRegisterManagerInfoColumnDefs({
    onClickViewDetail: (node: IRowNode<TypeOwnerSupplierManager>) => handleClickSupplierManagerModify(node),
  });

  const handleClickSupplierManagerModify = ({ data, rowIndex }: IRowNode) => {
    setIsVenderSupplierManagerModifyModalOpen(true);
    setSelectedManagerInfo({ ...data, rowIndex: rowIndex });
  };

  const handleClickSupplierManagerAdd = () => setIsVenderSupplierManagerAddModalOpen(true);

  const handleClickSupplierManagerDelete = () => {
    const selectedRowIndexList = selectedManagerRowsList.map(({ rowIndex }) => rowIndex);

    const deleteSelectedManagerRows = () => {
      const updatedManagerInfoList = venderSupplierAddForm.managerInfoList.filter(
        (_, index) => !selectedRowIndexList.includes(index)
      );

      setVenderSupplierAddForm({
        ...venderSupplierAddForm,
        managerInfoList: updatedManagerInfoList,
      });
    };

    dialogService.addDialog({
      type: 'confirm',
      children: '담당자 정보를 정말 삭제하시겠습니까?',
      confirmText: '삭제',
      onConfirm: deleteSelectedManagerRows,
    });
  };

  const handleVenderSupplierManagerAddModalCancel = () => setIsVenderSupplierManagerAddModalOpen(false);

  const handleVenderSupplierManagerAddModalConfirm = (managerInfo: TypeOwnerSupplierManager) => {
    setVenderSupplierAddForm({
      ...venderSupplierAddForm,
      managerInfoList: [...venderSupplierAddForm.managerInfoList, managerInfo],
    });
    setIsVenderSupplierManagerAddModalOpen(false);
  };

  const handleVenderSupplierManagerModifyModalCancel = () => setIsVenderSupplierManagerModifyModalOpen(false);

  const handleVenderSupplierManagerModifyModalConfirm = (managerInfo: unknown) => {
    const { rowIndex, ...updatedManagerInfo } = managerInfo as TypeManagerRowInfo;

    const updatedManagerInfoList = venderSupplierAddForm.managerInfoList.map((manager, index) => {
      if (index !== rowIndex) {
        return manager;
      } else {
        return updatedManagerInfo;
      }
    });

    setVenderSupplierAddForm({
      ...venderSupplierAddForm,
      managerInfoList: updatedManagerInfoList,
    });
    setIsVenderSupplierManagerModifyModalOpen(false);
  };

  return (
    <>
      <div className={styles['manager-info-section']}>
        <div className={styles['table-title-wrapper']}>
          <div className={styles['table-title-side-field']}>
            <Text size="xxlarge" weight="bold" element="span">
              담당자 정보
            </Text>
            <Text weight="semi-bold" color="tertiary" element="span">
              {`${venderSupplierAddForm.managerInfoList.length}/5`}
            </Text>
          </div>
          {
            <div className={styles['table-title-side-field']}>
              <Button
                size="small"
                color="tertiary"
                variant="outlined"
                leftIcon={<DeleteIcon size={16} />}
                disabled={selectedManagerRowsList.length === 0}
                onClick={handleClickSupplierManagerDelete}
              >
                삭제
              </Button>
              <Button
                size="small"
                color="secondary"
                leftIcon={<AddIcon color="white" size={16} />}
                disabled={venderSupplierAddForm.managerInfoList.length >= 5}
                onClick={handleClickSupplierManagerAdd}
              >
                추가
              </Button>
            </div>
          }
        </div>
        <div className={styles['table-wrapper']}>
          <Table
            data={venderSupplierAddForm.managerInfoList}
            options={{ columnDefs, ...gridOptions }}
            rowStatusType="SEARCH_EMPTY"
            onSelectionChange={(rows) => setSelectedManagerRowsList(rows as IRowNode<TypeOwnerSupplierManager>[])}
          />
        </div>
      </div>
      {isVenderSupplierManagerAddModalOpen && (
        <VenderSupplierManagerAddModal
          open={isVenderSupplierManagerAddModalOpen}
          onCancel={handleVenderSupplierManagerAddModalCancel}
          onConfirm={handleVenderSupplierManagerAddModalConfirm}
        />
      )}
      {isVenderSupplierManagerModifyModalOpen && (
        <VenderSupplierManagerModifyModal
          open={isVenderSupplierManagerModifyModalOpen}
          managerInfo={selectedManagerInfo}
          onCancel={handleVenderSupplierManagerModifyModalCancel}
          onConfirm={handleVenderSupplierManagerModifyModalConfirm}
        />
      )}
    </>
  );
};

export default VenderSupplierAddModalManagerInfoSection;
